<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed, watch } from 'vue';
import { useDiscordSdk } from './composables/useDiscordSdk';
import { useWebRTC } from './composables/useWebRTC';
import { getSignalingUrl } from './net';
import StreamPlayer from './components/StreamPlayer.vue';
import StreamControls from './components/StreamControls.vue';
import ParticipantList from './components/ParticipantList.vue';
import BroadcasterStudio from './components/BroadcasterStudio.vue';
import { useDesktopCompanion } from './composables/useDesktopCompanion';

const {
  isCompanionOnline,
  isCompanionStreaming,
  fetchSources,
  startDesktopStream,
  stopDesktopStream,
} = useDesktopCompanion();

const {
  isInsideDiscord,
  currentUser,
  channelId,
  userAvatar,
  userName,
  discordParticipants,
  initDiscord,
  loginDiscord,
  openBroadcasterInBrowser,
} = useDiscordSdk();

const {
  isStreaming,
  isReceiving,
  currentStreamer,
  participants,
  selectedQuality,
  connectionState,
  lastError,
  webrtcStatus,
  localStream,
  remoteStream,
  streamVideoUrl,
  connectSignaling,
  startScreenShare,
  stopStreaming,
  requestActiveStream,
  resyncStream,
  requestKeyframe,
  addParticipant,
  whepPlayer,
} = useWebRTC();

const whepStats = whepPlayer.stats;
const whepViewers = whepPlayer.viewers;
const whepStatus = whepPlayer.status;
const whepFullInfo = whepPlayer.fullInfo;
const whepErrorMessage = whepPlayer.errorMessage;

const isSelfStreamer = computed(() => {
  return (
    isStreaming.value ||
    isCompanionStreaming.value ||
    !!(currentStreamer.value?.isStreaming &&
      (currentStreamer.value.id === currentUser.value?.id ||
        (currentStreamer.value.name && currentStreamer.value.name.toLowerCase() === userName.value.toLowerCase()) ||
        currentStreamer.value.isSelf))
  );
});

const activeVideoStream = computed(() => whepPlayer.remoteVideoStream.value || remoteStream.value);
const activeAudioStream = computed(() => whepPlayer.remoteAudioStream.value || remoteStream.value);

const showSidebar = ref(true);

const signalingWorkerUrl = computed(() => {
  return getSignalingUrl();
});

// Participantes: usa os do Discord com prioridade se estiver no Discord, com deduplicação estrita
const effectiveParticipants = computed(() => {
  let list: any[] = [];
  if (isInsideDiscord.value && discordParticipants.value.length > 0) {
    list = discordParticipants.value.map((dp) => {
      const isStr =
        (currentStreamer.value?.isStreaming &&
          (currentStreamer.value?.id === dp.id || currentStreamer.value?.name === dp.name)) ||
        (dp.isSelf && isStreaming.value);
      return {
        ...dp,
        isStreaming: isStr,
      };
    });

    if (currentStreamer.value && currentStreamer.value.isStreaming) {
      const alreadyInList = list.some(
        (p) => p.id === currentStreamer.value?.id || p.name === currentStreamer.value?.name
      );
      if (!alreadyInList) {
        list.push(currentStreamer.value);
      }
    }
  } else {
    list = participants.value.map((p) => {
      const isStr =
        (currentStreamer.value?.isStreaming &&
          (currentStreamer.value?.id === p.id || currentStreamer.value?.name === p.name)) ||
        (p.isSelf && isStreaming.value);
      return {
        ...p,
        isStreaming: isStr,
      };
    });
  }

  // Deduplicação estrita por chave (Nome ou ID) para nunca exibir amigos duplicados
  const finalUnique: any[] = [];
  const seenKeys = new Set<string>();
  for (const p of list) {
    const key = p.name || p.id;
    if (!seenKeys.has(key)) {
      seenKeys.add(key);
      finalUnique.push(p);
    }
  }
  return finalUnique;
});

onMounted(async () => {
  await initDiscord();

  if (currentUser.value) {
    addParticipant(currentUser.value.id, userName.value, true);

    connectSignaling(
      signalingWorkerUrl.value,
      channelId.value,
      currentUser.value.id,
      userName.value
    );
  }

  // Se abriu no navegador com ?mode=broadcaster, deixa o Estúdio do Transmissor controlar o início
  // Não dispara startScreenShare() automaticamente para não roubar a transmissão do companion anti-eco
});

// Garante conexão à sinalização assim que o canal e perfil do Discord forem resolvidos
watch(
  [() => channelId.value, () => currentUser.value?.id],
  ([newChannel, newUserId]) => {
    if (newChannel && newUserId) {
      addParticipant(newUserId, userName.value, true);
      connectSignaling(
        signalingWorkerUrl.value,
        newChannel,
        newUserId,
        userName.value
      );
    }
  }
);

// Auto-reconexão inteligente para espectadores: se alguém está em live mas o vídeo ainda não chegou, solicita novamente
let connectingRetryInterval: number | undefined;

watch(
  [() => currentStreamer.value?.isStreaming, () => remoteStream.value, () => streamVideoUrl.value, () => isStreaming.value, () => whepPlayer.status.value],
  ([hasStreamer, hasRemote, hasUrl, amStreaming, whepStat]) => {
    if (connectingRetryInterval) {
      clearInterval(connectingRetryInterval);
      connectingRetryInterval = undefined;
    }

    if (isSelfStreamer.value) return;

    const isWhep = (currentStreamer.value as any)?.transport === 'whep-local';
    const isWhepActive = whepStat === 'playing' || whepStat === 'connecting';

    if (hasStreamer && !hasRemote && !hasUrl && !amStreaming && !isWhep && !isWhepActive) {
      console.log('[Nexia App] Streamer detectado, aguardando vídeo...');
      connectingRetryInterval = window.setInterval(() => {
        if (currentStreamer.value?.isStreaming && !remoteStream.value && !streamVideoUrl.value && !isWhep) {
          console.log('[Nexia App] Re-requisitando stream...');
          requestActiveStream();
        }
      }, 5000);
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  if (connectingRetryInterval) {
    clearInterval(connectingRetryInterval);
  }
});

function handleStartScreen(audioMode: 'system' | 'none' | 'device' = 'system', audioDeviceId?: string) {
  startScreenShare(audioMode, audioDeviceId);
}

function handleStopStream() {
  stopStreaming();
}

function handleWatchStream() {
  const isWhep = (currentStreamer.value as any)?.transport === 'whep-local';
  if (isWhep && currentStreamer.value) {
    whepPlayer.startPlaying(channelId.value, currentUser.value?.id || 'viewer', currentStreamer.value.id);
  } else {
    requestActiveStream();
  }
}

function handleOpenBrowser() {
  openBroadcasterInBrowser();
}

const isResyncing = ref(false);
function handleResync() {
  if (isResyncing.value) return;
  isResyncing.value = true;
  const isWhep = (currentStreamer.value as any)?.transport === 'whep-local';
  if (isWhep) {
    whepPlayer.retryNow();
  } else {
    resyncStream();
  }
  setTimeout(() => {
    isResyncing.value = false;
  }, 2500);
}

const isCopied = ref(false);
async function copyChannelId() {
  if (!channelId.value) return;
  try {
    await navigator.clipboard.writeText(channelId.value);
    isCopied.value = true;
    setTimeout(() => {
      isCopied.value = false;
    }, 2000);
  } catch (err) {
    console.warn('Falha ao copiar:', err);
  }
}
</script>

<template>
  <!-- SE ESTIVER FORA DO DISCORD (NO NAVEGADOR): Mostra exclusivamente o Estúdio do Transmissor -->
  <BroadcasterStudio
    v-if="!isInsideDiscord"
    :is-streaming="isStreaming"
    :current-streamer="currentStreamer"
    :channel-id="channelId"
    :user-name="userName"
    :user-avatar="userAvatar"
    :local-stream="localStream"
    :selected-quality="selectedQuality"
    :connection-state="connectionState"
    :last-error="lastError"
    @start-screen="(mode, deviceId) => handleStartScreen(mode, deviceId)"
    @stop-stream="handleStopStream"
    @update:quality="(q) => selectedQuality = q"
  />

  <!-- SE ESTIVER DENTRO DO DISCORD: Mostra a Atividade de Assistir (Modo Cinema / Espectador) -->
  <div v-else class="flex flex-col h-screen w-screen overflow-hidden text-[#dbdee1] bg-[#111214] select-none text-xs">
    <!-- Barra Superior Compacta (Header) -->
    <header class="h-11 px-3 border-b border-white/5 flex items-center justify-between bg-[#1e1f22] flex-shrink-0 z-20">
      <!-- Lado Esquerdo: Logo & Nome da Sala -->
      <div class="flex items-center gap-2.5">
        <div class="flex items-center gap-1.5">
          <div class="w-6 h-6 rounded-md bg-[#5865F2] flex items-center justify-center text-white shadow-xs">
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
          <span class="font-bold text-xs tracking-wide text-white font-sans">
            Nexia <span class="text-[#5865F2]">Live</span>
          </span>
        </div>

        <span class="text-white/10 text-xs hidden sm:inline">|</span>

        <!-- Tag Clicável para Copiar o ID da Sala -->
        <button
          @click="copyChannelId"
          class="hidden sm:flex items-center gap-1.5 px-2.5 py-0.5 rounded bg-black/30 hover:bg-[#5865F2]/20 border border-white/10 hover:border-[#5865F2]/40 text-[11px] text-[#dbdee1] hover:text-white transition cursor-pointer active:scale-95"
          :title="isCopied ? 'ID Copiado!' : 'Clique para copiar o ID da sala para colar no App Desktop'"
        >
          <svg class="w-3 h-3 text-[#5865F2]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 100-6 3 3 0 000 6z" />
          </svg>
          <span class="font-mono font-medium">{{ channelId }}</span>
          <span class="text-[9px] px-1.5 py-0.5 rounded font-bold" :class="isCopied ? 'bg-[#23a55a] text-white' : 'bg-white/10 text-[#949ba4]'">
            {{ isCopied ? '✓ Copiado' : 'Copiar' }}
          </span>
        </button>
      </div>

      <!-- Centro: Botão Sincronizar (visível apenas para espectadores quando há streamer ativo) -->
      <div class="flex-1 flex items-center justify-center">
        <button
          v-if="!isStreaming && currentStreamer && currentStreamer.isStreaming"
          @click="handleResync"
          :disabled="isResyncing"
          class="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-[#5865F2]/20 hover:bg-[#5865F2]/35 border border-[#5865F2]/40 text-[#5865F2] hover:text-white text-[11px] font-semibold transition cursor-pointer active:scale-95 disabled:opacity-50"
          title="Tela preta ou congelada? Clique para sincronizar imediatamente com a transmissão"
        >
          <svg
            class="w-3.5 h-3.5"
            :class="{ 'animate-spin': isResyncing }"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>{{ isResyncing ? 'Sincronizando...' : 'Sincronizar' }}</span>
        </button>
      </div>

      <!-- Lado Direito: Status, Usuário e Toggle da Sidebar -->
      <div class="flex items-center gap-2">
        <!-- Botão Parar Transmissão no Header se estiver transmitindo -->
        <button
          v-if="isSelfStreamer"
          @click="handleStopStream"
          class="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-[#f23f43] hover:bg-[#d83a3e] text-white text-[11px] font-bold transition cursor-pointer active:scale-95 shadow-sm"
        >
          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
          <span>Parar Transmissão</span>
        </button>

        <!-- Status do Anti-Eco no Header -->
        <div
          v-if="isCompanionOnline"
          class="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#23a55a]/15 border border-[#23a55a]/30 text-[#23a55a]"
          title="Nexia Companion conectado: áudio do Discord excluído automaticamente"
        >
          <span class="w-1.5 h-1.5 rounded-full bg-[#23a55a] animate-pulse"></span>
          <span>Anti-Eco Pronto</span>
        </div>

        <!-- Indicador de Sinalização -->
        <div
          class="flex items-center gap-1 px-2 py-0.5 rounded text-[11px]"
          :class="{
            'text-[#23a55a] bg-[#23a55a]/10': connectionState === 'connected',
            'text-[#f0b232] bg-[#f0b232]/10': connectionState === 'connecting',
            'text-[#f23f43] bg-[#f23f43]/10': connectionState === 'disconnected' || connectionState === 'error',
          }"
          :title="connectionState === 'connected' ? 'Sinal Conectado' : 'Conectando ao servidor...'"
        >
          <span
            class="w-1.5 h-1.5 rounded-full"
            :class="{
              'bg-[#23a55a] animate-pulse': connectionState === 'connected',
              'bg-[#f0b232]': connectionState === 'connecting',
              'bg-[#f23f43]': connectionState === 'disconnected' || connectionState === 'error',
            }"
          ></span>
          <span class="hidden md:inline font-medium">
            {{ connectionState === 'connected' ? 'Online' : 'Conectando' }}
          </span>
        </div>

        <!-- Usuário Atual Real do Discord -->
        <div class="flex items-center gap-1.5 pl-1">
          <img
            :src="userAvatar"
            alt="Avatar"
            class="w-6 h-6 rounded-full bg-[#2b2d31] object-cover"
          />
          <span class="text-xs font-medium text-[#dbdee1] hidden lg:inline truncate max-w-[90px]">
            {{ userName }}
          </span>
          <button
            v-if="isInsideDiscord && userName.startsWith('Amigo')"
            @click="loginDiscord"
            class="ml-1 px-2 py-0.5 rounded bg-[#5865F2] hover:bg-[#4752C4] text-white text-[10px] font-semibold transition cursor-pointer active:scale-95 shadow-xs"
            title="Conectar sua conta do Discord para exibir seu nome e foto reais"
          >
            Sincronizar Perfil
          </button>
        </div>

        <!-- Botão para alternar exibição da lista de participantes -->
        <button
          @click="showSidebar = !showSidebar"
          class="p-1 rounded text-[#949ba4] hover:text-white hover:bg-white/10 transition cursor-pointer"
          :title="showSidebar ? 'Ocultar Membros' : 'Mostrar Membros'"
        >
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </button>
      </div>
    </header>

    <!-- Banner de Diagnóstico / Erro -->
    <div
      v-if="lastError"
      class="mx-3 mt-2 px-3 py-2 rounded-md bg-[#f23f43]/15 border border-[#f23f43]/30 text-[#f23f43] text-xs flex items-center justify-between flex-shrink-0 animate-fade-in"
    >
      <div class="flex items-center gap-2">
        <svg class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span>{{ lastError }}</span>
      </div>
      <button
        @click="lastError = null"
        class="text-white/60 hover:text-white p-0.5 rounded hover:bg-white/10 transition cursor-pointer"
      >
        ✕
      </button>
    </div>

    <!-- Área Principal de Vídeo -->
    <main class="flex-1 flex overflow-hidden p-2 gap-2">
      <!-- Palco de Transmissão (Video Stage) -->
      <section class="flex-1 flex flex-col bg-black rounded-lg border border-white/5 overflow-hidden relative shadow-inner">
        <!-- 1: Stream Própria com Preview Local no Navegador -->
        <StreamPlayer
          v-if="isSelfStreamer && localStream"
          :video-stream="localStream"
          :is-local="true"
          :streamer-name="userName"
        />

        <!-- 2: Stream Própria via Desktop Companion (Painel do Transmissor Ao Vivo) -->
        <div
          v-else-if="isSelfStreamer"
          class="flex-1 flex flex-col items-center justify-center p-6 text-center select-none bg-gradient-to-b from-[#111214] to-black"
        >
          <div class="max-w-md w-full p-6 rounded-2xl bg-[#1e1f22]/95 border border-[#23a55a]/30 shadow-2xl space-y-4 animate-fade-in text-center">
            <!-- Badge Ao Vivo Pulsando -->
            <div class="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#23a55a]/15 border border-[#23a55a]/30 text-[#23a55a] text-xs font-bold">
              <span class="w-2 h-2 rounded-full bg-[#23a55a] animate-ping"></span>
              <span>VOCÊ ESTÁ TRANSMITINDO AO VIVO</span>
            </div>

            <div class="space-y-1">
              <h2 class="text-base font-bold text-white">Transmissão em Andamento</h2>
              <p class="text-xs text-[#949ba4] leading-relaxed">
                Sua tela e jogos estão sendo transmitidos em tempo real para os amigos no canal de voz via OBS Engine (Anti-Eco Ativo).
              </p>
            </div>

            <!-- Métricas / Status -->
            <div class="grid grid-cols-2 gap-2.5 text-left text-xs bg-black/50 p-3 rounded-xl border border-white/10 font-mono">
              <div>
                <span class="text-[#949ba4] text-[10px] block">CANAL DA SALA:</span>
                <span class="text-white font-bold text-[11px] truncate block">{{ channelId }}</span>
              </div>
              <div>
                <span class="text-[#949ba4] text-[10px] block">ESPECTADORES:</span>
                <span class="text-[#23a55a] font-bold text-[11px] block">👥 {{ whepViewers.current || 0 }} de {{ whepViewers.max || 2 }}</span>
              </div>
            </div>

            <!-- Botão Encerrar Transmissão -->
            <button
              @click="handleStopStream"
              class="w-full py-2.5 rounded-xl bg-[#f23f43] hover:bg-[#d83a3e] text-white text-xs font-bold transition flex items-center justify-center gap-2 cursor-pointer active:scale-98 shadow-md"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span>Encerrar Transmissão</span>
            </button>
          </div>
        </div>

        <!-- 3: Assistindo ao Stream de Alguém (Modo Espectador via WHEP / WebRTC / WebSocket) -->
        <StreamPlayer
          v-else-if="currentStreamer && currentStreamer.isStreaming"
          :video-stream="activeVideoStream"
          :video-url="streamVideoUrl"
          :audio-stream="activeAudioStream"
          :is-local="false"
          :streamer-name="currentStreamer?.name || 'Amigo'"
          :stats="whepStats"
          :viewers="whepViewers"
          :is-full="whepStatus === 'full'"
          :full-info="whepFullInfo"
          :whep-status="whepStatus"
          :error-message="whepErrorMessage"
          @retry="() => { if (currentStreamer) whepPlayer.startPlaying(channelId, currentUser?.id || 'viewer', currentStreamer.id); }"
        />

        <!-- 3: Lobby Limpo (Ninguém transmitindo) -->
        <div
          v-else
          class="flex-1 flex flex-col items-center justify-center p-4 text-center select-none"
        >
          <div class="max-w-md w-full space-y-4">
            <div class="w-14 h-14 mx-auto rounded-2xl bg-[#5865F2]/10 border border-[#5865F2]/30 flex items-center justify-center text-[#5865F2] shadow-md">
              <svg class="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>

            <div>
              <h2 class="text-sm font-bold text-white">
                Pronto para transmitir ou assistir no Discord
              </h2>
              <p class="text-[11px] text-[#949ba4] mt-1 leading-relaxed">
                Transmita seus jogos e tela com qualidade total diretamente para os amigos nesta chamada.
              </p>
            </div>

            <!-- Card de Auxílio para o App Desktop com Copiar ID -->
            <div class="p-2.5 rounded-xl bg-[#1e1f22] border border-white/10 flex items-center justify-between gap-3 text-left">
              <div class="min-w-0">
                <span class="text-[10px] text-[#949ba4] uppercase font-bold block">ID da Sala para o App:</span>
                <span class="font-mono text-xs text-white font-bold truncate block">{{ channelId }}</span>
              </div>
              <button
                @click="copyChannelId"
                class="px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer active:scale-95 shadow-sm whitespace-nowrap flex-shrink-0"
                :class="isCopied ? 'bg-[#23a55a] text-white' : 'bg-[#5865F2] hover:bg-[#4752C4] text-white'"
              >
                <span>{{ isCopied ? '✓ Copiado!' : '📋 Copiar ID da Sala' }}</span>
              </button>
            </div>



            <!-- Botões de Início no Centro do Lobby -->
            <div class="pt-2 flex justify-center">
              <StreamControls
                :is-streaming="isStreaming || isCompanionStreaming"
                :current-streamer="currentStreamer"
                :is-inside-discord="isInsideDiscord"
                :selected-quality="selectedQuality"
                :channel-id="channelId"
                :user-name="userName"
                @start-screen="() => handleStartScreen('system')"
                @open-browser="handleOpenBrowser"
                @stop-stream="handleStopStream"
                @update:quality="(q) => selectedQuality = q"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- Sidebar Direita: Membros Reais do Discord -->
      <aside v-if="showSidebar" class="w-52 flex-shrink-0">
        <ParticipantList
          :participants="effectiveParticipants"
          :current-streamer="currentStreamer"
          :is-receiving="isReceiving"
          :is-streaming="isStreaming || isCompanionStreaming"
          @watch-stream="handleWatchStream"
        />
      </aside>
    </main>

  </div>
</template>
